import React, { useEffect, useState } from 'react';
import { Card, Button, Progress, Select, Spin, Result } from 'antd';
import { useTranslation } from 'react-i18next';
import { useIntroModal } from '../../core/context/IntroModalContext';
import { useHistory } from 'react-router-dom';
import ChangelogFrame from './ChangelogFrame';

const { Option } = Select;

export default function ChangelogWizard() {
    const { t, i18n } = useTranslation();
    const history = useHistory();
    const { missedVersions, isLoading, markAsSeen, currentVersion } = useIntroModal();
    const [allSteps, setAllSteps] = useState([]);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [iframeLoading, setIframeLoading] = useState(true);
    const [selectedLanguage, setSelectedLanguage] = useState((i18n.language || 'tr').substring(0, 2));

    // Flatten steps from missed versions
    useEffect(() => {
        if (!missedVersions || missedVersions.length === 0) {
            return;
        }

        const flattened = [];
        missedVersions.forEach(ver => {
            if (ver.steps && ver.steps.length > 0) {
                ver.steps.forEach((step, idx) => {
                    flattened.push({
                        version: ver.version,
                        stepData: step,
                        indexInVersion: idx + 1,
                        totalInVersion: ver.steps.length,
                        isLastGlobal: false
                    });
                });
            }
        });

        if (flattened.length > 0) {
            flattened[flattened.length - 1].isLastGlobal = false;
        }

        // Add Final Step
        flattened.push({
            type: 'final',
            version: 'Done',
            indexInVersion: flattened.length + 1,
            totalInVersion: flattened.length + 1
        });

        setAllSteps(flattened);
    }, [missedVersions]);

    // Swipe support
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            if (currentStepIndex < allSteps.length - 1) {
                handleNext();
            }
        }
        if (isRightSwipe) {
            handlePrev();
        }
    };

    // Handle iframe load
    const handleIframeLoad = React.useCallback(() => {
        setIframeLoading(false);
    }, []);

    // Navigation
    const handleFinish = React.useCallback(async () => {
        if (currentVersion) {
            await markAsSeen();
        }
        history.push('/');
    }, [currentVersion, markAsSeen, history]);

    const handleNext = React.useCallback(async () => {
        if (currentStepIndex < allSteps.length - 1) {
            setIframeLoading(true);
            setCurrentStepIndex(prev => prev + 1);
        }
    }, [currentStepIndex, allSteps.length]);

    const handlePrev = React.useCallback(() => {
        if (currentStepIndex > 0) {
            setIframeLoading(true);
            setCurrentStepIndex(prev => prev - 1);
        }
    }, [currentStepIndex]);

    // Keyboard support
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowRight') {
                handleNext();
            } else if (e.key === 'ArrowLeft') {
                handlePrev();
            } else if (e.key === 'Enter') {
                if (currentStepIndex === allSteps.length - 1) {
                    handleFinish();
                }
            }
        };

        const handleMessage = (e) => {
            if (e.data && e.data.type === 'CHANGELOG_KEYDOWN') {
                handleKeyDown({ key: e.data.key });
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('message', handleMessage);
        };
    }, [handleNext, handlePrev, handleFinish, currentStepIndex, allSteps.length]);

    // If no steps or loading
    if (isLoading) {
        return <div style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }}><Spin size="large" /></div>;
    }

    if (!missedVersions || missedVersions.length === 0) {
        return (
            <div style={{ padding: 50 }}>
                <Result
                    status="success"
                    title={t("You are up to date!")}
                    subTitle={`${t('Current version')}: ${currentVersion}`}
                    extra={[
                        <Button type="primary" key="console" onClick={() => history.push('/')}>
                            {t('Go to Dashboard')}
                        </Button>,
                    ]}
                />
            </div>
        );
    }

    const currentStep = allSteps[currentStepIndex];
    if (!currentStep) return null;

    const { version, stepData } = currentStep;
    const progressPercent = Math.round(((currentStepIndex) / (allSteps.length - 1)) * 100);

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: '#f0f2f5',
                padding: '20px'
            }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
        >
            <Card
                title={
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span>{t("What's New")}</span>
                                <Select
                                    value={selectedLanguage}
                                    onChange={(val) => setSelectedLanguage(val)}
                                    size="small"
                                    style={{ width: 100 }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Option value="tr">Türkçe</Option>
                                    <Option value="en">English</Option>
                                </Select>
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#888' }}>
                                {currentStep.type === 'final' ? t('Completed') : version}
                            </span>
                        </div>
                        <Progress percent={progressPercent} showInfo={false} size="small" />
                    </div>
                }
                style={{
                    width: '100%',
                    maxWidth: 900,
                    maxHeight: 'calc(100vh - 40px)',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
                bodyStyle={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    padding: currentStep.type === 'final' ? '24px' : '0'
                }}
            >
                {currentStep.type === 'final' ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <h2>{t('You are all up to date!')}</h2>
                        <p>{t('Click the button below to start using the new version.')}</p>
                        <Button type="primary" size="large" onClick={handleFinish} style={{ marginTop: 20 }}>
                            {t('Start Using')}
                        </Button>
                    </div>
                ) : (
                    <>
                        <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                            {iframeLoading && (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                                    <Spin tip="Loading content..." />
                                </div>
                            )}
                            <ChangelogFrame
                                version={version}
                                file={`${stepData}.html`}
                                onLoad={handleIframeLoad}
                                loading={iframeLoading}
                                setLoading={setIframeLoading}
                                lang={selectedLanguage}
                            />
                        </div>

                        <div style={{
                            textAlign: 'right',
                            borderTop: '1px solid #f0f0f0',
                            padding: '16px 24px',
                            backgroundColor: '#fff',
                            flexShrink: 0
                        }}>
                            {currentStepIndex > 0 && (
                                <Button style={{ marginRight: 8 }} onClick={handlePrev}>
                                    {t('Previous')}
                                </Button>
                            )}
                            <Button type="primary" onClick={handleNext} size="large">
                                {t('Next')}
                            </Button>
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
}
